// Navigation aus den Manifesten der Module (design-system §3.2): Gruppen in fester Reihenfolge, Einträge nach
// `order`; was ohne Recht oder mit abgeschalteter Funktion nicht nutzbar wäre, erscheint nicht.
// Reine Funktionen – Sidebar, untere Leiste, Brotkrumen und Befehlspalette bauen alle hierauf.
import { LayoutDashboard } from 'lucide-react'
import { passesGate } from '../api/permissions'
import type { SessionPayload } from '../api/types'
import { NAV_SECTIONS, type BottomBarSlot, type IconComponent, type NavBadge, type NavSection, type WebModule } from '../module'

export interface NavItem {
  /** eindeutig: '<modul>:<pfad>' */
  key: string
  label: string
  icon: IconComponent
  to: string
  section: NavSection
  order: number
  /** beanspruchter Platz der unteren Leiste am Telefon: 'work' = Platz 2, 'lookup' = Platz 4 (NavEntry.mobile) */
  mobile: BottomBarSlot | null
  /**
   * Rang innerhalb des Platzes: 2 = die Person hält das Arbeitsrecht des Eintrags (`mobile.write`), 1 = der Eintrag
   * nennt keines, 0 = sie darf hier nur lesen
   */
  mobileRank: 0 | 1 | 2
  badge?: NavBadge
}

export interface NavGroup {
  key: NavSection
  label: string
  items: NavItem[]
}

export interface Navigation {
  /** ohne Gruppe, ganz oben: Start, Aufgaben */
  top: NavItem[]
  /** Gruppen mit mindestens einem sichtbaren Eintrag, in der Reihenfolge des Design-Systems */
  groups: NavGroup[]
  /** unten in der Sidebar: Einstellungen */
  settings: NavItem[]
  /** alles in Anzeige-Reihenfolge */
  all: NavItem[]
}

export const START_ITEM: NavItem = {
  key: 'shell:/',
  label: 'Start',
  icon: LayoutDashboard,
  to: '/',
  section: 'top',
  order: -1000,
  // Platz 1 gehört fest der Startseite – kein beanspruchbarer Platz
  mobile: null,
  mobileRank: 1,
}

const byOrder = (a: NavItem, b: NavItem): number => a.order - b.order || a.label.localeCompare(b.label, 'de')

type Access = Pick<SessionPayload, 'permissions' | 'activeLocationId' | 'tenant' | 'actions'>

/**
 * Sichtbare Navigation für eine Sitzung. Ein Eintrag erscheint, wenn sein Recht an irgendeinem Standort gilt und
 * sein Funktionsschalter an ist. Zähler entfallen, wenn die Abfrage dahinter nicht erlaubt ist.
 */
export function buildNavigation(modules: readonly WebModule[], session: Access | null): Navigation {
  const items: NavItem[] = [START_ITEM]
  for (const module of modules) {
    for (const entry of module.nav) {
      if (!passesGate(session, entry, 'any')) continue
      const badge = entry.badge && session?.actions.includes(entry.badge.query.name) ? entry.badge : undefined
      items.push({
        key: `${module.id}:${entry.to}`,
        label: entry.label,
        icon: entry.icon,
        to: entry.to,
        section: entry.section,
        order: entry.order ?? 500,
        mobile: entry.mobile === true ? 'work' : typeof entry.mobile === 'object' ? entry.mobile.slot : entry.mobile || null,
        mobileRank: typeof entry.mobile === 'object' ? (passesGate(session, { permission: entry.mobile.write }, 'any') ? 2 : 0) : 1,
        badge,
      })
    }
  }
  const inSection = (section: NavSection): NavItem[] => items.filter((item) => item.section === section).sort(byOrder)
  const top = inSection('top')
  const settings = inSection('settings')
  const groups = NAV_SECTIONS.filter((section) => section.key !== 'top' && section.key !== 'settings')
    .map((section) => ({ key: section.key, label: section.label, items: inSection(section.key) }))
    .filter((group) => group.items.length > 0)
  return { top, groups, settings, all: [...top, ...groups.flatMap((group) => group.items), ...settings] }
}

const matches = (to: string, pathname: string): boolean => {
  const target = to.split(/[?#]/)[0] ?? to
  return target === '/' ? pathname === '/' : pathname === target || pathname.startsWith(`${target}/`)
}

/** Der Eintrag zur aktuellen Seite – bei mehreren Treffern der genaueste (/stock/transfers vor /stock). */
export function activeNavItem(items: readonly NavItem[], pathname: string): NavItem | null {
  let best: NavItem | null = null
  for (const item of items) {
    if (!matches(item.to, pathname)) continue
    if (!best || item.to.length > best.to.length) best = item
  }
  return best
}

export interface BottomBarPlan {
  /** Plätze 1 und 2 (links von der Mitte) */
  leading: NavItem[]
  /** Platz 4 (rechts von der Mitte) – null: dort steht die Suche */
  trailing: NavItem | null
}

/**
 * Untere Leiste auf dem Telefon (design-system §3.9): fünf Plätze – Start · 2 · Scannen · 4 · Mehr.
 * Die Regel, einmal: Platz 2 nimmt der erste sichtbare Eintrag mit `mobile: 'work'`, Platz 4 der erste mit
 * `mobile: 'lookup'` (Reihenfolge der Navigation; sichtbar heißt: Recht und Funktionsschalter der Person). Nennt
 * ein Eintrag sein Arbeitsrecht (`mobile: { slot, write }`), geht er vor, wenn die Person es hält, und zurück, wenn
 * sie dort nur lesen darf – so steht beim Fahrer „Lieferungen“ auf Platz 2, obwohl er auch Artikel lesen darf. Ein
 * freier Platz 2 zeigt „Aufgaben“, ein freier Platz 4 die „Suche“. Die Suche bleibt in jedem Fall erreichbar:
 * als erster Eintrag unter „Mehr“ (MobileNavSheet, `more-search`).
 */
export function planBottomBar(navigation: Navigation): BottomBarPlan {
  // der höchste Rang gewinnt, bei Gleichstand die Reihenfolge der Navigation
  const first = (slot: BottomBarSlot): NavItem | null =>
    navigation.all.reduce<NavItem | null>((best, item) => (item.mobile === slot && (!best || item.mobileRank > best.mobileRank) ? item : best), null)
  const work = first('work')
  return { leading: work ? [START_ITEM, work] : [START_ITEM], trailing: first('lookup') }
}

export interface Crumb {
  label: string
  href?: string
}

/** Brotkrumen der Topbar: Gruppe › Seite. */
export function breadcrumbsFor(navigation: Navigation, pathname: string, title: string | undefined): Crumb[] {
  const active = activeNavItem(navigation.all, pathname)
  const group = active ? navigation.groups.find((entry) => entry.key === active.section) : undefined
  const crumbs: Crumb[] = []
  if (group) crumbs.push({ label: group.label })
  if (active && title && title !== active.label && matches(active.to, pathname) && pathname !== active.to) {
    crumbs.push({ label: active.label, href: active.to })
  }
  crumbs.push({ label: title ?? active?.label ?? 'Start' })
  return crumbs
}
